import type { RunnerDisplayState, RunnerObstacle, RunnerObstacleType, RunnerAnimationType } from '../../../domain/RunnerDisplayState.ts';
import type { RunnerStageAssets } from '../../../games/runner/stages/almaty/almatyStage.ts';
import { drawLiveSkeletonPts } from '../room/skeleton.ts';
import { drawCalibBackdrop, drawRemoteCalibrationStandZone } from '../room/calibrationShared.ts';
import { defaultRemoteViewLayout } from 'shared/lib/tv-game/nativeLayout.ts';
import type { CalibNormMap } from '../../../domain/RemoteDisplayState.ts';

// ─── Perspective constants (from Android RunnerGameObjectsView) ───────────────
const VP_X_RATIO = 0.5;
export const VP_Y_RATIO = 0.59;
const ROAD_LEFT_BOTTOM_RATIO = 0.0;
const ROAD_RIGHT_BOTTOM_RATIO = 1.0;
const PERSPECTIVE_NEAR = 250;
const PERSPECTIVE_Z_MAX = 1000;
const PERSPECTIVE_T_MIN = PERSPECTIVE_NEAR / (PERSPECTIVE_NEAR + PERSPECTIVE_Z_MAX);
const PLAYER_Z = 880;

// Sprite sheet: /public/images/games/runner/player_sprite_sheet.png
const SPRITE_SHEET_URL = '/images/games/runner/player_sprite_sheet.png';

// Base height as fraction of canvas height (mirrors Android: baseHeight = h * 0.2)
const BASE_HEIGHT_RATIO = 0.2;
const JUMP_HEIGHT_RATIO = 1.1;

interface SpriteFrame { x: number; y: number; w: number; h: number }

const WALK_FRAMES: SpriteFrame[] = [
  { x: 6,    y: 23,  w: 78,  h: 166 },
  { x: 128,  y: 23,  w: 80,  h: 166 },
  { x: 239,  y: 24,  w: 79,  h: 165 },
  { x: 353,  y: 22,  w: 81,  h: 166 },
  { x: 464,  y: 23,  w: 84,  h: 166 },
  { x: 579,  y: 23,  w: 81,  h: 166 },
  { x: 690,  y: 24,  w: 85,  h: 164 },
  { x: 808,  y: 23,  w: 78,  h: 165 },
  { x: 917,  y: 23,  w: 79,  h: 166 },
  { x: 1031, y: 22,  w: 80,  h: 166 },
  { x: 1139, y: 24,  w: 76,  h: 165 },
];
const JUMP_FRAMES: SpriteFrame[] = [
  { x: 25,   y: 284, w: 70,  h: 142 },
  { x: 131,  y: 304, w: 83,  h: 122 },
  { x: 268,  y: 267, w: 91,  h: 158 },
  { x: 417,  y: 243, w: 86,  h: 152 },
  { x: 557,  y: 243, w: 91,  h: 138 },
  { x: 700,  y: 267, w: 88,  h: 159 },
  { x: 848,  y: 295, w: 79,  h: 131 },
  { x: 973,  y: 301, w: 80,  h: 125 },
  { x: 1090, y: 296, w: 79,  h: 130 },
];
const PUNCH_FRAMES: SpriteFrame[] = [
  { x: 18,  y: 483, w: 81,  h: 156 },
  { x: 137, y: 487, w: 99,  h: 152 },
  { x: 276, y: 485, w: 124, h: 154 },
  { x: 414, y: 485, w: 129, h: 154 },
  { x: 555, y: 485, w: 134, h: 154 },
  { x: 725, y: 485, w: 84,  h: 154 },
  { x: 869, y: 484, w: 72,  h: 155 },
];
const CROUCH_FRAMES: SpriteFrame[] = [
  { x: 21,   y: 691, w: 98,  h: 136 },
  { x: 138,  y: 722, w: 117, h: 103 },
  { x: 284,  y: 752, w: 167, h: 64  },
  { x: 482,  y: 758, w: 164, h: 58  },
  { x: 672,  y: 765, w: 152, h: 51  },
  { x: 847,  y: 752, w: 153, h: 64  },
  { x: 1024, y: 696, w: 101, h: 126 },
  { x: 1140, y: 690, w: 108, h: 132 },
];

function framesForAnimation(type: RunnerAnimationType): SpriteFrame[] {
  switch (type) {
    case 'JUMP':  return JUMP_FRAMES;
    case 'DUCK':  return CROUCH_FRAMES;
    case 'PUNCH': return PUNCH_FRAMES;
    default:      return WALK_FRAMES;
  }
}

function frameForProgress(frames: SpriteFrame[], progress: number): SpriteFrame {
  const idx = Math.floor(Math.max(0, Math.min(0.9999, progress)) * frames.length);
  return frames[idx];
}

export class RunnerCanvasLayer {
  private readonly ctx: CanvasRenderingContext2D;
  private readonly spriteSheet: HTMLImageElement;
  /** `null` в 3D-режиме: дорогу, игрока и препятствия рисует `Runner3DScene` в своей канве под этой. */
  private readonly bgVideo: HTMLVideoElement | null;
  private readonly obstacleImages = new Map<RunnerObstacleType | 'PUNCH_DESTROYED', HTMLImageElement>();
  private assetsLoaded = false;

  constructor(private readonly canvas: HTMLCanvasElement, assets: RunnerStageAssets, useVideo = true) {
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('no 2d ctx');
    this.ctx = ctx;

    this.spriteSheet = new Image();
    this.spriteSheet.src = SPRITE_SHEET_URL;

    if (useVideo) {
      const video = document.createElement('video');
      video.src = assets.backgroundVideoUrl;
      video.loop = true;
      video.muted = true;
      video.playsInline = true;
      video.preload = 'auto';
      video.style.display = 'none';
      document.body.appendChild(video);
      this.bgVideo = video;
    } else {
      this.bgVideo = null;
    }

    this.loadObstacleImage('JUMP', assets.obstacleJumpUrl);
    this.loadObstacleImage('DUCK', assets.obstacleDuckUrl);
    this.loadObstacleImage('PUNCH', assets.obstaclePunchUrl);
    this.loadObstacleImage('DODGE', assets.obstacleDodgeUrl);
    this.loadObstacleImage('PUNCH_DESTROYED', assets.obstaclePunchDestroyedUrl);

    this.assetsLoaded = true;
  }

  private loadObstacleImage(key: RunnerObstacleType | 'PUNCH_DESTROYED', url: string): void {
    const img = new Image();
    img.src = url;
    this.obstacleImages.set(key, img);
  }

  startVideo(): void {
    if (!this.bgVideo) return;
    this.bgVideo.currentTime = 0;
    void this.bgVideo.play().catch(() => {});
  }

  resumeVideo(): void {
    void this.bgVideo?.play().catch(() => {});
  }

  pauseVideo(): void {
    this.bgVideo?.pause();
  }

  stopVideo(): void {
    if (!this.bgVideo) return;
    this.bgVideo.pause();
    this.bgVideo.currentTime = 0;
  }

  destroy(): void {
    this.bgVideo?.pause();
    this.bgVideo?.remove();
  }

  private drawCalibration(state: RunnerDisplayState, W: number, H: number): void {
    const ctx = this.ctx;
    const layout = defaultRemoteViewLayout(W, H);
    ctx.save();
    ctx.beginPath();
    ctx.rect(layout.offsetX, layout.offsetY, layout.gameWidth, layout.gameHeight);
    ctx.clip();
    drawCalibBackdrop(ctx, layout);
    drawRemoteCalibrationStandZone(ctx, layout, 'square');
    if (state.calibKeypoints.size > 0) {
      drawLiveSkeletonPts(ctx, state.calibKeypoints as unknown as CalibNormMap, layout, false, 0, 210, 255, false);
    }
    ctx.restore();
  }

  render(state: RunnerDisplayState): void {
    const W = this.canvas.width;
    const H = this.canvas.height;
    const ctx = this.ctx;

    if (state.phase === 'calibration') {
      ctx.clearRect(0, 0, W, H);
      this.drawCalibration(state, W, H);
      return;
    }

    if (state.phase !== 'playing' && state.phase !== 'paused' && state.phase !== 'gameover') {
      ctx.clearRect(0, 0, W, H);
      return;
    }

    ctx.clearRect(0, 0, W, H);

    // 3D mode: the whole track is drawn by Runner3DScene under the cleared canvas
    if (!this.bgVideo) return;

    if (this.bgVideo.readyState >= 2) {
      ctx.drawImage(this.bgVideo, 0, 0, W, H);
    } else {
      ctx.fillStyle = '#1a1a2e';
      ctx.fillRect(0, 0, W, H);
    }

    const frame = state.frame;
    if (!frame) return;

    const vpX = W * VP_X_RATIO;
    const vpY = H * VP_Y_RATIO;
    const roadLeft = W * ROAD_LEFT_BOTTOM_RATIO;
    const roadRight = W * ROAD_RIGHT_BOTTOM_RATIO;

    this.drawLaneLines(vpX, vpY, roadLeft, roadRight, H, W);

    const playerZ = frame.player.playerZ;
    const behindPlayer = frame.obstacles.filter(o => o.zPos < playerZ);
    const inFrontOfPlayer = frame.obstacles.filter(o => o.zPos >= playerZ);

    // Draw back-to-front
    [...behindPlayer].sort((a, b) => a.zPos - b.zPos)
      .forEach(o => this.drawObstacle(o, vpX, vpY, roadLeft, roadRight, H));

    this.drawPlayer(frame.player.lane, frame.player.animationType, frame.player.animationProgress, vpX, vpY, roadLeft, roadRight, H);

    [...inFrontOfPlayer].sort((a, b) => a.zPos - b.zPos)
      .forEach(o => this.drawObstacle(o, vpX, vpY, roadLeft, roadRight, H));
  }

  private perspectiveT(zPos: number): number {
    const rawT = PERSPECTIVE_NEAR / (PERSPECTIVE_NEAR + (PERSPECTIVE_Z_MAX - zPos));
    return Math.max(0, Math.min(1, (rawT - PERSPECTIVE_T_MIN) / (1 - PERSPECTIVE_T_MIN)));
  }

  private drawLaneLines(vpX: number, vpY: number, roadLeft: number, roadRight: number, H: number, W: number): void {
    const ctx = this.ctx;
    ctx.save();
    ctx.strokeStyle = 'rgba(255,255,255,0.4)';
    ctx.lineWidth = Math.max(2, W * 0.004);
    ctx.lineCap = 'round';

    const sectionW = (roadRight - roadLeft) / 3;
    // left border
    ctx.beginPath(); ctx.moveTo(vpX, vpY); ctx.lineTo(roadLeft, H); ctx.stroke();
    // divider 1
    ctx.beginPath(); ctx.moveTo(vpX, vpY); ctx.lineTo(roadLeft + sectionW, H); ctx.stroke();
    // divider 2
    ctx.beginPath(); ctx.moveTo(vpX, vpY); ctx.lineTo(roadLeft + sectionW * 2, H); ctx.stroke();
    // right border
    ctx.beginPath(); ctx.moveTo(vpX, vpY); ctx.lineTo(roadRight, H); ctx.stroke();
    ctx.restore();
  }

  private drawObstacle(obstacle: RunnerObstacle, vpX: number, vpY: number, roadLeft: number, roadRight: number, H: number): void {
    if (obstacle.animationProgress >= 1) return;
    const t = this.perspectiveT(obstacle.zPos);
    if (t <= 0) return;

    const roadLeftAtZ = vpX + (roadLeft - vpX) * t;
    const roadRightAtZ = vpX + (roadRight - vpX) * t;
    const roadWidthAtZ = roadRightAtZ - roadLeftAtZ;
    const sectionWidthAtZ = roadWidthAtZ / 3;

    const lane = Math.max(0, Math.min(2, obstacle.lane));
    const objLeft = roadLeftAtZ + lane * sectionWidthAtZ;
    const objRight = objLeft + sectionWidthAtZ;
    const objWidth = objRight - objLeft;
    const baseY = vpY + (H - vpY) * t;

    const imgKey: RunnerObstacleType | 'PUNCH_DESTROYED' =
      obstacle.isDestroyed && obstacle.type === 'PUNCH' ? 'PUNCH_DESTROYED' : obstacle.type;
    const img = this.obstacleImages.get(imgKey);
    const ctx = this.ctx;
    ctx.save();
    if (obstacle.isDestroyed) {
      ctx.globalAlpha = Math.max(0, 1 - obstacle.animationProgress);
    }
    if (img && img.complete && img.naturalWidth > 0) {
      const aspect = img.naturalHeight / img.naturalWidth;
      const objHeight = objWidth * aspect;
      ctx.drawImage(img, objLeft, baseY - objHeight, objWidth, objHeight);
    } else {
      const obstacleColors: Record<string, string> = {
        JUMP: '#ef4444', DUCK: '#f97316', PUNCH: '#8b5cf6', DODGE: '#06b6d4', PUNCH_DESTROYED: '#fbbf24',
      };
      const color = obstacleColors[imgKey] ?? '#ffffff';
      const objHeight = objWidth * 1.2;
      ctx.fillStyle = color;
      ctx.globalAlpha = (ctx.globalAlpha ?? 1) * 0.85;
      ctx.fillRect(objLeft, baseY - objHeight, objWidth, objHeight);
    }
    ctx.restore();
  }

  private drawPlayer(
    lane: number,
    animationType: RunnerAnimationType,
    animationProgress: number,
    vpX: number, vpY: number,
    roadLeft: number, roadRight: number,
    H: number,
  ): void {
    const t = this.perspectiveT(PLAYER_Z);
    const roadLeftAtZ = vpX + (roadLeft - vpX) * t;
    const roadRightAtZ = vpX + (roadRight - vpX) * t;
    const sectionWidthAtZ = (roadRightAtZ - roadLeftAtZ) / 3;

    const clampedLane = Math.max(0, Math.min(2, lane));
    const laneCentreX = roadLeftAtZ + (clampedLane + 0.5) * sectionWidthAtZ;
    const baseY = vpY + (H - vpY) * t;

    const frames = framesForAnimation(animationType);
    const frame = frameForProgress(frames, animationProgress);

    const spriteSheet = this.spriteSheet;
    const baseHeight = H * BASE_HEIGHT_RATIO;

    if (!spriteSheet.complete || spriteSheet.naturalWidth === 0) {
      const scale = baseHeight / 166;
      const avatarWidth = 80 * scale;
      const avatarHeight = baseHeight;
      const left = laneCentreX - avatarWidth / 2;
      this.ctx.save();
      this.ctx.fillStyle = 'rgba(255,200,60,0.85)';
      this.ctx.fillRect(left, baseY - avatarHeight, avatarWidth, avatarHeight);
      this.ctx.restore();
      return;
    }

    // baseSpriteScale is derived once from the first walk frame (mirrors Android setBaseScale())
    const refFrame = WALK_FRAMES[0];
    const baseSpriteScale = baseHeight / refFrame.h;
    const avatarWidth = frame.w * baseSpriteScale;
    const avatarHeight = frame.h * baseSpriteScale;

    // Jump sine arc (matches Android sin(progress * π) * avatarHeight * JUMP_HEIGHT_RATIO)
    let jumpOffsetY = 0;
    if (animationType === 'JUMP') {
      jumpOffsetY = Math.sin(animationProgress * Math.PI) * avatarHeight * JUMP_HEIGHT_RATIO;
    }

    const left = laneCentreX - avatarWidth / 2;
    const bottom = baseY - jumpOffsetY;
    const top = bottom - avatarHeight;

    this.ctx.drawImage(
      spriteSheet,
      frame.x, frame.y, frame.w, frame.h,
      left, top, avatarWidth, avatarHeight,
    );
  }
}
